import { useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Icon, type IconName } from "@/components/Icon";
import { HelpText } from "@/components/HelpText";
import { canUseCamera, hasNativeCamera } from "@/camera";
import { CameraCapture } from "@/components/CameraCapture";
import { PhoneQr } from "@/components/PhoneQr";
import { InfoTip } from "@/components/InfoTip";
import { devPreview } from "@/features";
import { type HelpKey, useHelp, useShowTips } from "@/help";
import { Sheet } from "@/components/Sheet";
import { named, ReceiptAccept, receiptFiles, type ReceiptVia } from "@/receiptFiles";

/** Whether the browser lets a button read pictures or PDFs from the clipboard. */
const canReadClipboard =
  typeof navigator !== "undefined" && typeof navigator.clipboard?.read === "function";

async function receiptsFromClipboard(): Promise<File[]> {
  const files: File[] = [];
  for (const item of await navigator.clipboard.read()) {
    const type = item.types.find((t) => t.startsWith("image/") || t === "application/pdf");
    if (type) files.push(named(new File([await item.getType(type)], "", { type })));
  }
  return files;
}

/**
 * Every way to add to a draft report, in one place: forwarding, dragging, and a button each for a
 * photo, the clipboard, a file, typing it in, and mileage. Photos, pastes and files join the same
 * queue as dropped receipts, so several can be read at once.
 */
export function AddPanel({
  forwardTo,
  pendingCount,
  onFiles,
  onManual,
  onMileage,
  onPending,
}: {
  forwardTo: string | null | undefined;
  pendingCount: number;
  onFiles: (files: File[], via: ReceiptVia) => void;
  onManual: () => void;
  onMileage: () => void;
  onPending: () => void;
}) {
  const camera = useRef<HTMLInputElement>(null);
  const upload = useRef<HTMLInputElement>(null);
  // Phones open their own camera app from the file input; computers get a live camera here.
  const [webcam, setWebcam] = useState(false);
  const [note, setNote] = useState<string>();

  // On a computer, the photo tip has a QR code for this report with ?snap=1. Opened on a phone,
  // that asks to open the camera: the phone needs a tap before it will.
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const phoneLink = hasNativeCamera()
    ? undefined
    : `${window.location.origin}${location.pathname}?snap=1`;
  const snap = params.has("snap") && hasNativeCamera();
  function doneSnapping() {
    setParams(
      (current) => {
        current.delete("snap");
        return current;
      },
      { replace: true },
    );
  }

  function take(list: FileList | null, input: HTMLInputElement | null, via: ReceiptVia) {
    const files = receiptFiles(list);
    if (input) input.value = "";
    if (files.length > 0) {
      setNote(undefined);
      onFiles(files, via);
    } else if (list && list.length > 0) {
      setNote("Choose a photo, a PDF or an email of a receipt.");
    }
  }

  function paste() {
    if (!canReadClipboard) {
      setNote("Press Ctrl+V (⌘V on a Mac) anywhere on this page to paste a receipt.");
      return;
    }
    receiptsFromClipboard()
      .then((files) => {
        if (files.length > 0) {
          setNote(undefined);
          onFiles(files, "Paste");
        } else setNote("There's no picture or PDF on the clipboard to paste.");
      })
      .catch(() =>
        setNote("The browser didn't allow pasting. Press Ctrl+V (⌘V on a Mac) instead."),
      );
  }

  type Action = "camera" | "paste" | "upload" | "manual" | "mileage";
  const help = useHelp();
  const showTips = useShowTips();
  const buttons: { action: Action; icon: IconName; label: string; short: string; tip: HelpKey }[] =
    [
      {
        action: "camera",
        icon: "camera",
        label: "Snap a Photo of It",
        short: "Photo",
        tip: hasNativeCamera() ? "tipPhotoPhone" : "tipPhoto",
      },
      {
        action: "paste",
        icon: "paste",
        label: "Paste It",
        short: "Paste",
        tip: "tipPaste",
      },
      {
        action: "upload",
        icon: "upload",
        label: "Upload It",
        short: "Upload",
        tip: "tipUpload",
      },
      {
        action: "manual",
        icon: "pencil",
        label: "Manually Enter It",
        short: "Manual",
        tip: "tipManual",
      },
      {
        action: "mileage",
        icon: "car",
        label: "Enter Mileage",
        short: "Mileage",
        tip: "tipMileage",
      },
    ];
  // On a phone, the button whose explanation is showing under the row.
  const [tipFor, setTipFor] = useState<Action>();
  const shownTip = buttons.find((button) => button.action === tipFor);

  function act(action: Action) {
    if (action === "camera") {
      if (!hasNativeCamera() && canUseCamera()) setWebcam(true);
      else camera.current?.click();
    } else if (action === "upload") upload.current?.click();
    else if (action === "paste") paste();
    else if (action === "manual") onManual();
    else onMileage();
  }

  return (
    <section className="card add-panel">
      <div className="add-panel-head">
        <h2>Multiple Ways to Add Receipts, Expenses, and Mileage</h2>
        {showTips && (
          <InfoTip label="How adding works">
            <HelpText text={help("addIntro")} />
          </InfoTip>
        )}
      </div>
      <ul className="add-ways">
        {forwardTo &&
          (devPreview ? (
            <li>
              <Icon name="mail" size={18} />
              <span>
                Forward your receipts to <a href={`mailto:${forwardTo}`}>{forwardTo}</a>
              </span>
              {showTips && (
                <InfoTip label="How forwarding works">
                  <HelpText text={help("forwarding")} />
                </InfoTip>
              )}
            </li>
          ) : (
            <li>
              <Icon name="mail" size={18} />
              <span>
                Forward your email receipts to <a href={`mailto:${forwardTo}`}>{forwardTo}</a> and
                they will be automatically processed and added to the Expense Report for you.
              </span>
            </li>
          ))}
        <li className="pointer-only">
          <Icon name="drag" size={18} />
          <span>Drag the receipt anywhere on this page (emails, photos, or PDFs).</span>
          {devPreview && showTips && (
            <InfoTip label="How dragging works">
              <HelpText text={help("dragging")} />
            </InfoTip>
          )}
        </li>
      </ul>
      <div className="add-buttons">
        {buttons.map((button) => (
          <div key={button.action} className="add-button-wrap">
            <button
              type="button"
              className="add-button"
              aria-label={button.label}
              aria-describedby={devPreview && showTips ? `add-tip-${button.action}` : undefined}
              onClick={() => act(button.action)}
            >
              <Icon name={button.icon} size={20} />
              {/* The full words where there's room; one word each on a phone, so all five fit. */}
              <span className="add-button-long">{button.label}</span>
              <span className="add-button-short" aria-hidden="true">
                {button.short}
              </span>
            </button>
            {devPreview && showTips && (
              <>
                {/* With a mouse: shown above the button while it's hovered or focused. */}
                <span id={`add-tip-${button.action}`} role="tooltip" className="add-tip">
                  <HelpText text={help(button.tip)} />
                  {button.action === "camera" && phoneLink && (
                    <PhoneTip link={phoneLink} text={help("tipPhotoQr")} />
                  )}
                </span>
                {/* A small (i) in the corner shows it under the row. */}
                <button
                  type="button"
                  className="add-tip-dot"
                  aria-label={`What ${button.label} does`}
                  aria-expanded={tipFor === button.action}
                  onClick={() => setTipFor(tipFor === button.action ? undefined : button.action)}
                >
                  <Icon name="info" size={16} />
                </button>
              </>
            )}
          </div>
        ))}
      </div>
      {devPreview && showTips && shownTip && (
        <div className="add-tip-line" role="status">
          <HelpText text={help(shownTip.tip)} lead={<strong>{shownTip.short}:</strong>} />
          {shownTip.action === "camera" && phoneLink && (
            <PhoneTip link={phoneLink} text={help("tipPhotoQr")} />
          )}
        </div>
      )}
      {pendingCount > 0 && (
        <button type="button" className="choice" onClick={onPending}>
          <span className="choice-icon">
            <Icon name="mail" size={22} />
          </span>
          <span>
            <strong>Emailed receipts ({pendingCount})</strong>
            <span className="muted">Already read, waiting for a report</span>
          </span>
          <Icon name="chevronRight" size={18} />
        </button>
      )}
      {note && (
        <p className="muted small" role="status">
          {note}
        </p>
      )}
      <input
        ref={camera}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => take(e.target.files, e.target, "Photo")}
      />
      <input
        ref={upload}
        type="file"
        accept={ReceiptAccept}
        multiple
        hidden
        onChange={(e) => take(e.target.files, e.target, "Upload")}
      />
      <Sheet open={snap} title="Snap a Photo of your Receipt" onClose={doneSnapping}>
        <div className="stack">
          <p>The photo is read and added to this report, just as on your computer.</p>
          <button
            type="button"
            className="btn"
            onClick={() => {
              // In the tap itself: the phone only opens its camera for one.
              camera.current?.click();
              doneSnapping();
            }}
          >
            <Icon name="camera" size={18} /> Open the camera
          </button>
        </div>
      </Sheet>
      <Sheet open={webcam} title="Snap a Photo of your Receipt" onClose={() => setWebcam(false)}>
        {webcam && (
          <CameraCapture
            onPhoto={(file) => onFiles([file], "Photo")}
            onChooseFile={() => {
              setWebcam(false);
              upload.current?.click();
            }}
          />
        )}
      </Sheet>
    </section>
  );
}

/** On a computer: a QR code that opens this report on a phone, ready to snap a receipt. */
function PhoneTip({ link, text }: { link: string; text: string }) {
  return (
    <span className="phone-tip">
      <PhoneQr link={link} />
      {text && <HelpText text={text} />}
    </span>
  );
}
